import React from "react";
import {
  Component, Container, HBox, Position, PuzzleButton, VBox,
} from 'puzzle';
import { Switch } from "@progress/kendo-react-inputs";
import DueSoonOverdueGrid from "../Widgets/DueSoonOverdue/DueSoonOverdueGrid";
import _ from "lodash";
import Statistics from "./Statistics/Statistics";
import { CONFIGURATION_STATUSES } from "../Types";

type PROPS = {
  dueSoonRange: number,
  quickLinks: any
};

type STATE = {
  activeTab: boolean
};

const statusMapper = {
  "chartSegmentCompletedFilter": "COMPLETED_SUBMISSION_STATUS",
  "chartSegmentDeliveredFilter": "DELIVERED_SUBMISSION_STATUS",
  "chartSegmentOverdueFilter": "OVERDUE",
  "chartSegmentDueSoonFilter": "DUESOON",
  "chartSegmentActiveFilter": "ACTIVE_SUBMISSION_STATUS",
  "chartSegmentOnHoldFilter": "ON_HOLD_SUBMISSION_STATUS"
}

class Widgets extends Component<PROPS, STATE> {
  state = {
    activeTab: true //if true overDue is active, on false due is active
  }


  /**
   * returns single quick link item configuration
   *
   * @param item
   * @param index
   */
  getQuickLinks = (item: any, index: number) => {
    let linkObject: {
      icon: string,
      title: string,
      text: string,
      tooltip: string,
      link: string
    };
    const { quickLinks } = this.props

    switch (item) {
      case 'shareThoughts':
        linkObject = {
          icon: 'comments',
          title: this.translate('shareThoughts.title'),
          text: this.translate('shareThoughts.text'),
          tooltip: this.translate('shareThoughts.tooltip'),
          link: quickLinks['glKnowledgeIdeas']
        }
        break;
      case 'newInRelease':
        linkObject = {
          icon: 'whats-new',
          title: this.translate('newInRelease.title'),
          text: this.translate('newInRelease.text'),
          tooltip: this.translate('newInRelease.tooltip'),
          link: quickLinks['quickLink.helpCenterReleases.path']
        }
        break;
      case 'supportRequest':
        linkObject = {
          icon: 'no-file-found',
          title: this.translate('trackSupport.title'),
          text: this.translate('trackSupport.text'),
          tooltip: this.translate('trackSupport.tooltip'),
          link: quickLinks['quickLink.trackSupportRequests.path']
        }
        break;
      default:
        linkObject = {
          icon: '',
          title: '',
          text: '',
          tooltip: '',
          link: ''
        }
        break;
    }

    return (
      <HBox
        alignItemsCenter
        className={'pd-quick-link-item'}
        gap={2}
        key={`pd-quick-link-item-${index}`}
      >
        <span className={`pd-widget-quick-link-img ${linkObject.icon}`} />
        <VBox flex1>
          <h3
            tabIndex={0}
            className={'tp-font-size tp-color-accent tp-text-semibold tp-pointable'}
            onClick={() => this.handleQuickLinkClick(linkObject.link)}
          >
            {linkObject.title}
          </h3>
          <Container className={'pd-quick-link-text'}>
            {linkObject.text}
            <PuzzleButton
              titleText={linkObject.tooltip}
              onClick={() => this.handleQuickLinkClick(linkObject.link)}
              aria-label={linkObject.tooltip}
              iconClass={'tp-icon tp-arrow-chevron-right'}
              fillMode={'flat'}
              extraSmall
              tooltipClassName={'k-align-self-end'}
              tooltipPosition={'left'}
              withTooltip
            />
          </Container>
        </VBox>
      </HBox>
    )
  }
  /**
   * Handles opening quick links
   * @param link
   */
  handleQuickLinkClick = (link: string) => {
    if (link) {
      window.open(link, '_blank');
    }
  };

  /**
   * Set currently active tab e.g. Overdue/Due soon
   */
  setActiveTab = ((responseData: any) => {
    let chartSegmentDueDateFilter = _.find(responseData.gridConfig, { dataIndex: 'chartSegmentDueDateFilter' });
    if (chartSegmentDueDateFilter) {
      const activeTab = !chartSegmentDueDateFilter.hidden;
      this.setState({ activeTab });
    }
    const chartData = _.filter(responseData.gridConfig, (item) => _.startsWith(item.dataIndex, "chartSegment") && !item.hidden && (item.dataIndex !== 'chartSegmentDueDateFilter'));
    if (chartData.length) {
      const requestedSubmissionsPieData = chartData.map(({ dataIndex }: {
        dataIndex: CONFIGURATION_STATUSES
      }) => statusMapper[dataIndex]);
      this.dispatch('getChartData', {
        dispatcherId: 'chartDispatcher',
        pos: Position.end
      }, {
        payload: {
          folder: 'DEFAULT_SUBMISSION',
          requestedSubmissionsPieData
        },
        chartUrl: '/submissionsPieChart'
      })
    } else {
      this.dispatch('loadEmptyChartData', Position.end)
    }
  });

  /**
   * Changes switch state
   */
  onSwitchChange = () => this.setState({ activeTab: !this.state.activeTab }, () => {
    this.dispatch('dueSoonOverdueRequest', '', {
      dataIndex: 'chartSegmentDueDateFilter',
      hidden: !this.state.activeTab,
      withMainGridChange: true
    });
  });

  renderBody() {
    const { activeTab } = this.state;
    let links = ['shareThoughts', 'newInRelease', 'supportRequest'];

    return (
      <HBox className={'pd-dashboard-widgets-section'} gap={4} tabIndex={0} htmlTag={'section'}>
        <VBox className={'pd-widget-wrapper pd-statistics-widget'} flex1>
          <h2 className={'pd-widget-title tp-text-medium tp-font-size-xl'}>
            {this.translate('statistics')}
          </h2>
          <Statistics />
        </VBox>
        <Container className={'pd-widget-wrapper pd-due-dates-widget'} flex1>
          <VBox className={'pd-due-dates-widget-content'} justifyContentBetween alignItemsStart>
            <HBox className={'tp-100'} justifyContentBetween>
              <h2 className={'pd-widget-title tp-text-medium tp-font-size-xl'}>
                {this.translate('dueDates')}
              </h2>
              <Switch
                name={'widgetDueDates'}
                onChange={this.onSwitchChange}
                checked={activeTab}
                onLabel={this.translate('overdue')}
                offLabel={this.translate('dueSoon')}
              />
            </HBox>
            <Container
              className={'pd-due-dates-widget-subtitle'}>{activeTab ? this.translate('overDueSubTitle') : this.translate('dueSoonSubTitle')}</Container>
            <DueSoonOverdueGrid
              activeTab={activeTab}
              dueSoonRange={this.props.dueSoonRange}
            />
          </VBox>
        </Container>
        <VBox className={'pd-widget-wrapper pd-quick-links-widget'} flex1>
          <h2 className={'pd-widget-title tp-text-medium tp-font-size-xl'}>
            {this.translate('quickLinks')}
          </h2>
          <VBox gap={4} className={'pd-quick-links-wrapper'}>
            {links.map((item: string, index: number) => {
              return this.getQuickLinks(item, index)
            })}
          </VBox>
        </VBox>
      </HBox>
    )
  };
};

export default Widgets;
